/**
 * LoginView, Clerk sign-in.
 *
 * S-055 / FEAT-006. The previous email/password form is replaced with
 * Clerk's hosted `<SignIn>` component. The surrounding marketing
 * panel and brand framing are preserved so the screen stays on-brand
 * during the cutover.
 *
 * RequireAuth already redirects unauthenticated users into Clerk's
 * flow; this view is reachable when a user navigates directly to
 * `/login` or after sign-out. It also handles the `after sign-in
 * redirect` back to the kanban view.
 */

import { SignIn, Waitlist } from "@clerk/clerk-react";

/**
 * The six public stages, in order, with their ramp colours. The sign-in page
 * names stages, not the agents behind them (founder brief, 2026-11-01).
 */
const PUBLIC_STAGES = [
  { label: "Research", color: "#D4F1F4" },
  { label: "Brief creation", color: "#A7DFE8" },
  { label: "Drafting", color: "#99BFB8" },
  { label: "Verification", color: "#8FA998" },
  { label: "Optimization", color: "#5FA8B0" },
  { label: "Publishing", color: "#2B6777" },
] as const;

const BETA_URL = "https://thecmolab.ai/nagareru/beta/";
const PRIVACY_URL = "https://thecmolab.ai/privacy/";

/** Clerk card styling: Riverflow tokens, and our own heading instead of Clerk's logo. */
export const SIGN_IN_APPEARANCE = {
  variables: {
    colorPrimary: "#2B6777",
    colorForeground: "#132D34",
    colorBackground: "#FFFFFF",
    fontFamily: "Satoshi, ui-sans-serif, system-ui, sans-serif",
    borderRadius: "8px",
  },
  elements: {
    logoBox: { display: "none" },
    footerAction: { display: "none" },
    formButtonPrimary: {
      backgroundColor: "#2B6777",
      backgroundImage: "none",
      "&:hover": { backgroundColor: "#1F4E5A" },
    },
  },
};

/** The river mark, the same path as thecmolab.ai's assets/nagareru-mark.svg. */
function RiverMark({ size = 32 }: { size?: number }) {
  const d = "m 8.6,81.6 c 22,-30 37,-30 47,-15 10,15 25,20 35,0 10,-20 25,-20 42,-5";
  return (
    <svg width={size} height={size} viewBox="0 0 140 140" fill="none" aria-hidden>
      <path d={d} stroke="#5FA8B0" strokeWidth="7" strokeLinecap="round" />
      <path d={d} stroke="#2B6777" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

/**
 * The shell both auth screens share.
 *
 * S-377 designed this page; the waitlist page arrived later and must not be a
 * second copy of it that drifts. Same brand framing, same stage list, only the
 * heading and the Clerk component change.
 */
function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: React.ReactNode;
  subtitle: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="login-wrap auth-page">
      <div className="login-card">
        <div className="login-brand">
          <RiverMark />
          <span className="login-brand-name">nagareru</span>
          <span className="login-brand-by">by The CMO Lab</span>
        </div>
        <h1 className="login-title">{title}</h1>
        <p className="login-sub">{subtitle}</p>

        {children}

        {footer}
        <p className="login-legal">
          nagareru, by The CMO Lab
          {" · "}
          <a href={PRIVACY_URL}>Privacy</a>
          {" · "}
          <a href={BETA_URL}>Beta terms</a>
        </p>
      </div>
      <div className="login-side">
        <p className="login-side-eyebrow">Every piece runs the same current</p>
        <ol className="login-agents">
          {PUBLIC_STAGES.map((stage) => (
            <li key={stage.label} className="login-agent">
              <span className="login-agent-dot" style={{ background: stage.color }} aria-hidden />
              {stage.label}
            </li>
          ))}
        </ol>
        <p className="login-side-close">
          Six stages, one current. You choose where it stops for your review,
          from none to every stage.
        </p>
      </div>
    </div>
  );
}

export function LoginView() {
  return (
    <AuthShell
      title={
        <>
          Pick up where <em>the current</em> left off.
        </>
      }
      subtitle="Sign in to your workspace."
      footer={
        <p className="login-apply">
          New to nagareru? <a href={BETA_URL}>Apply for the beta</a>
        </p>
      }
    >
      <SignIn
        routing="path"
        path="/login"
        // BUG-077, was "/sign-up", a route this SPA has never had. Clerk sent
        // anyone clicking it to the hosted Account Portal at
        // hot-raccoon-76.accounts.dev, which is the cryptic URL the founder
        // saw. In waitlist mode this is the "request access" link.
        signUpUrl="/join"
        afterSignInUrl="/kanban"
        afterSignUpUrl="/onboarding"
        appearance={SIGN_IN_APPEARANCE}
      />
    </AuthShell>
  );
}

/**
 * BUG-077, the request-access page, on this domain.
 *
 * The instance is in waitlist mode: signing up means asking to be let in. With
 * no route of our own, Clerk hosted that form at hot-raccoon-76.accounts.dev:
 * an unbranded URL shown to the one visitor most worth impressing.
 */
export function JoinView() {
  return (
    <AuthShell
      title="Request access"
      subtitle="Tell us where to reach you. We review every request by hand."
    >
      <Waitlist afterJoinWaitlistUrl="/login" appearance={SIGN_IN_APPEARANCE} />
    </AuthShell>
  );
}
